<script setup lang="ts">
  import { Briefcase, UserCog } from 'lucide-vue-next';
  import { Accordion, Switch } from '@/shared/ui';
  import DirectoriesPermissionSection from './DirectoriesPermissionSection.vue';
  import type { DirectoryNodePermissions } from '../model/types';

  const props = withDefaults(
    defineProps<{
      permissions: DirectoryNodePermissions;
      canToggleSuperadmin: boolean;
      canApplyPositions?: boolean;
      canClearPositions?: boolean;
      canApplyFunctionalActivities?: boolean;
      canClearFunctionalActivities?: boolean;
    }>(),
    {
      canApplyPositions: false,
      canClearPositions: false,
      canApplyFunctionalActivities: false,
      canClearFunctionalActivities: false,
    }
  );

  const emit = defineEmits<{
    (e: 'toggle-superadmin'): void;
    (e: 'toggle-position', positionId: string): void;
    (e: 'toggle-functional-activity', activityId: string): void;
    (e: 'toggle-positions-select-all', positionIds: string[]): void;
    (e: 'toggle-functional-activities-select-all', activityIds: string[]): void;
    (e: 'apply-positions'): void;
    (e: 'apply-functional-activities'): void;
    (e: 'clear-positions'): void;
    (e: 'clear-functional-activities'): void;
  }>();
</script>

<template>
  <div data-testid="directories-permissions-tab" class="flex flex-col gap-3">
    <div v-if="canToggleSuperadmin" class="flex items-center justify-between gap-3 py-1">
      <span class="text-sm font-medium text-gray-07">Superadmin</span>
      <Switch
        :model-value="permissions.superadmin"
        aria-label="Superadmin"
        @update:model-value="emit('toggle-superadmin')"
      />
    </div>

    <Accordion
      type="multiple"
      :default-value="['positions', 'functionalActivities']"
      class="flex flex-col gap-3"
    >
      <DirectoriesPermissionSection
        value="positions"
        title="Cargos"
        :header-icon="Briefcase"
        header-icon-label="Cargos"
        select-label="Selecione os cargos :"
        select-all-label="Cargos"
        count-label="Total de cargos selecionados:"
        :entries="props.permissions.positions"
        :can-apply="props.canApplyPositions"
        :can-clear="props.canClearPositions"
        @toggle-entry="emit('toggle-position', $event)"
        @toggle-select-all="emit('toggle-positions-select-all', $event)"
        @apply="emit('apply-positions')"
        @clear="emit('clear-positions')"
      />
      <DirectoriesPermissionSection
        value="functionalActivities"
        title="Atividades Funcionais"
        :header-icon="UserCog"
        header-icon-label="Atividades Funcionais"
        select-label="Selecione as Atividades Funcionais :"
        select-all-label="Atividades Funcionais"
        count-label="Total de atividades selecionadas:"
        :entries="props.permissions.functionalActivities"
        :can-apply="props.canApplyFunctionalActivities"
        :can-clear="props.canClearFunctionalActivities"
        @toggle-entry="emit('toggle-functional-activity', $event)"
        @toggle-select-all="emit('toggle-functional-activities-select-all', $event)"
        @apply="emit('apply-functional-activities')"
        @clear="emit('clear-functional-activities')"
      />
    </Accordion>
  </div>
</template>
